<template>

<div class="relative">

    <section class="relative z-30" ref="container">

        <component-wrapper @loaded="contentLoaded" :data="data"></component-wrapper>

       <div class="absolute inset-0 w-full h-full group">
           <div v-if="isNotFooter && isNotMenu" @dblclick="edit"
                class="invisible h-full w-full overflow-hidden flex items-start justify-end pt-48"
                style="background-color: rgba(0,0,0,0.3);" :class="componentClass">
               <div class="mr-48 mb-48 inline-flex flex-col items-end space-y-8" ref="buttons">
                   <simple-button
                       v-if="can.delete && notDefaultComponent"
                       display="danger"
                       look="icon"
                       icon="delete"
                       id="delete-block"
                       @click="confirmDelete"
                   />
                   <simple-button
                       v-if="can.update && isNotFooter"
                       look="icon"
                       icon="edit"
                       id="edit-block"
                       @click="edit"
                   />
                   <simple-button
                       v-if="can.update && possibleStyles.length"
                       look="icon"
                       icon="dashboard"
                       id="layout-block"
                       @click="switchLayout"
                   />
               </div>
           </div>
       </div>

    </section>

    <div v-if="can.create && isNotFooter && isNotMenu" class="absolute bottom-0 translate-y-1/2 flex items-center w-full justify-center xxl:justify-start z-40">

        <simple-button
            class="absolute xxl:-ml-76 peer z-top"
            display="neutral"
            look="icon"
            icon="add"
            @click="startAddingComponent(data.hasOwnProperty('order') ? data.order + 1 : 0)"
        />
        <div class="absolute h-2 bg-primary w-full hidden peer-hover:block"></div>
        <div class="absolute h-2 bg-primary w-40 -ml-40 hidden peer-hover:block"></div>
    </div>

    <popup-modal-confirm
        v-if="can.delete && deleting"
        :confirmText="$t('general.delete_subject', {subject: $t('site_editor.block')})"
        :message="$t('general.confirm_delete_subject', {subject: $t('site_editor.this_block')})"
        show-exclamation
        :working="performingDeleteRequest"
        @confirm="deleteComponent(deleting.id)"
        @close="cancelDelete"
    ></popup-modal-confirm>

    <layout-switcher
        v-if="possibleStyles.length && switchingLayout"
        @close="stopSwitchingLayout"
        :styles="possibleStyles"
        :current-style="data.style"
        :component="data"
    ></layout-switcher>

</div>

</template>

<script>
import {mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useBrandingStore} from '../../shared/state/branding';
import ComponentWrapper from '../../shared/components/jobsite/ComponentWrapper.vue';
import SvgIcon from './ui/SvgIcon.vue';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import * as components from '../services/ComponentService';
import Component from '../models/Component';
import ScrollMagic from 'scrollmagic';
import RoundButton from './ui/RoundButton.vue';
import LayoutSwitcher from './ui/LayoutSwitcher.vue';
import SimpleButton from "./ui/SimpleButton.vue";
import '../../shared/alpine';

export default {
    components: {
        SimpleButton,
        'component-wrapper': ComponentWrapper,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'round-button': RoundButton,
        'layout-switcher': LayoutSwitcher,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        canCreate: {type: Boolean, default: true},
        canEdit: {type: Boolean, default: true},
    },
    data: function () {
        return {
            isNotFooter: this.data.type !== 'site-footer',
            isNotHeader: this.data.type !== 'site-header',
            isNotMenu: this.data.type !== 'site-menu',
            deleting: null,
            performingDeleteRequest: false,
            switchingLayout: false,
        }
    },
    computed: {
        notDefaultComponent () {
            return this.isNotFooter && this.isNotHeader && this.isNotMenu && this.data.type !== 'jobs';
        },
        can () {
            return {
                view: this.$can(['jobsites.view', 'jobsites.manage']),
                create: this.canCreate ? this.$can('jobsites.create') : false,
                update: this.canEdit ? this.$can('jobsites.update') : false,
                delete: this.canEdit ? this.$can('jobsites.delete') : false,
            };
        },
        componentClass () {
            let canConfigure = this.notDefaultComponent ? (this.can.update || this.can.delete) : this.can.update;

            return canConfigure ? 'group-hover:visible' : null;
        },
        possibleStyles () {
            switch (this.data.type) {
                case 'jobs':
                    return [
                        { name: 'grid', title: this.$t('components.styles.grid'), icon: 'gallery-thumbnail' },
                        { name: 'grid_without_images', title: this.$t('components.styles.grid_without_images'), icon: 'grid-view' },
                        { name: 'rows', title: this.$t('components.styles.rows'), icon: 'splitscreen-top' },
                        { name: 'rows_without_images', title: this.$t('components.styles.rows_without_images'), icon: 'view-agenda' },
                    ]
                    break;
                case 'values':
                    return [
                        { name: 'full_width', title: this.$t('components.styles.full_width'), icon: 'gallery-thumbnail' },
                        { name: 'shadow_grid', title: this.$t('components.styles.shadow_grid'), icon: 'gallery-shadow' },
                    ]
                    break;
                // case 'team':
                //     return [
                //         { name: 'classic', title: this.$t('components.styles.team_classic'), icon: 'componentstyle-grid' },
                //         { name: 'grid', title: this.$t('components.styles.team_grid'), icon: 'componentstyle-full-width-grid' },
                //     ]
                //     break;
                default:
                    return [];
            }
        }
    },
    methods: {
        ...mapActions(useBrandingStore, ['startAddingComponent', 'startConfiguringComponent', 'startConfiguringHeader']),
        confirmDelete () {
            this.deleting = this.data;
        },
        cancelDelete () {
            this.deleting = null;
        },
        edit () {
            if (this.isNotHeader) {
                this.editComponent();
            } else {
                this.editHeader();
            }
        },
        editComponent () {
            this.startConfiguringComponent(this.data);
        },
        editHeader () {
            this.startConfiguringHeader();
        },
        deleteComponent (id) {
            this.performingDeleteRequest = true;

            components.destroy(id).then(() => {
                const data = useRepo(Component)
                    .where((c) => {
                        return c.company_id === this.data.company_id && c.order > this.data.order;
                    })
                    .get()
                    .map((c) => {
                        return {
                            id: c.id,
                            order: c.order - 1
                        };
                    });
                useRepo(Component).save(data);
                useRepo(Component).destroy(id);

            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            })
        },
        contentLoaded () {
            if (this.isNotMenu && this.isNotFooter) {
                setTimeout(() => {
                    // this.pinButtons();
                }, 300);
            }
        },
        pinButtons () {

            if (this._controller) {
                this._controller.destroy(true);
            }

            this._controller = new ScrollMagic.Controller();

            if(this.$refs.container){
                new ScrollMagic.Scene({
                    triggerElement: this.$refs.container,
                    duration: this.$refs.container.offsetHeight - (this.$refs.buttons.offsetHeight + 96),
                    triggerHook: 0,
                    offset: -192,
                })
                .setPin(this.$refs.buttons)
                .addTo(this._controller);
            }
        },
        switchLayout () {
            this.switchingLayout = true;
        },
        stopSwitchingLayout () {
            this.switchingLayout = false;
        }
    },
    beforeUnmount () {
        if (this._controller) {
            this._controller.destroy(true);
        }
    }
}
</script>
